import { ZuiFormAssociatedElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { style } from './zui-slider-css.js';

const DEFAULT_MAX = 100;

/**
 * A range form control for choosing values along a slider.
 * @element zui-slider
 *
 * @attr {string | null} [name=null] - The name of this element that is associated with form submission
 * @attr {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 * @attr {boolean} [readonly=false] - Represents whether a user can make changes to this element; the value of this element will still be included in the form submission
 * @attr {boolean} [autofocus=false] - If true, this element will be focused when connected to the document
 * @attr {number} [value=50] - Represents the value of the input. Can be set to a default value, and will reflect the value provided by the user when interactive with the control
 *
 * @prop {string | null} [name=null] - The name of this element that is associated with form submission
 * @prop {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 * @prop {boolean} [readOnly=false] - Represents whether a user can make changes to this element; the value of this element will still be included in the form submission
 * @prop {boolean} [autofocus=false] - If true, this element will be focused when connected to the document
 * @prop {number} [valueAsNumber=50] - Returns the value of the element, interpreted as one of the following, in order: A number, NaN if conversion is impossible
 * @prop {string} [value='50'] - Represents the value of the input. Can be set to a default value, and will reflect the value provided by the user when interactive with the control
 * @prop {number} [progress=50] - Determines visual placement of the slider thumb along the line
 *
 * @cssprop [--zui-slider-thumb-size=1.875rem (30px)] - Point of contact to grab and slide to change value
 *
 * @event {CustomEvent} change - Fires when value changes, details contain `value`
 */
export class ZuiSlider extends ZuiFormAssociatedElement {
  protected get _focusControlSelector() {
    return 'input';
  }

  protected get _formValue() {
    return this.value;
  }

  protected formResetCallback() {
    this.value = this.#defaultValue;
  }

  #defaultValue = `${DEFAULT_MAX / 2}`;

  #value = `${DEFAULT_MAX / 2}`;

  @property()
  get value() {
    return this.#value;
  }

  set value(val) {
    const oldVal = this.#value;
    val = this._ensureValidValue(val);
    this.#value = val;
    this._setFormValue(this.#value);
    this.requestUpdate('value', oldVal);
  }

  get valueAsNumber() {
    return parseFloat(this.#value);
  }

  /**
   * Represents the maximum permitted value
   */
  @property({ type: Number }) min = 0;

  /**
   * Represents the maximum permitted value
   */
  @property({ type: Number, attribute: 'max' }) max = 100;

  /**
   * Represents the stepping interval, used both for user interface and validation purposes
   */
  @property({ type: Number }) step = 0;

  /**
   * Represents that this control must be filled in for form submission
   */
  @property({ type: Boolean, attribute: 'no-text' }) noText = false;

  get progress() {
    return ((this.valueAsNumber - this.min) / (this.max - this.min)) * 100;
  }

  static get styles() {
    return [super.styles, style];
  }

  connectedCallback() {
    super.connectedCallback();

    // we want to go a little faster than LitElement and behave more like native HTML Form Associated Elements
    let value = this.getAttribute('value') ?? this.value;
    const max = this.getAttribute('max') ?? this.max;
    const min = this.getAttribute('min') ?? this.min;

    value = this._ensureValidValue(value, min, max);

    this.#defaultValue = value;
    this._setFormValue(value.toString());
  }

  render() {
    const progressColor = this.disabled ? 'var(--zui-gray)' : 'var(--zui-blue)';
    const styles = {
      background: `linear-gradient(to right, ${progressColor} 0%, ${progressColor} ${this.progress}%, var(--zui-gray-200) ${this.progress}%, var(--zui-gray-200) 100%)`,
    };
    return html`<input
        style=${styleMap(styles)}
        type="range"
        .value="${this.value}"
        .min="${this.min}"
        .max="${this.max}"
        .step="${this.step}"
        ?disabled="${this.disabled}"
        @input="${this._onInput}"
        @change="${this._onChange}"
      />${this._renderText()}`;
  }

  private _renderText() {
    if (this.noText) {
      return html``;
    }
    const thumbOffset = `var(--zui-slider-thumb-size) * ${this.progress / 100}`;
    const styles = {
      left: `calc(${this.progress}% - ${thumbOffset} + 0.125rem)`,
    };
    return html`<span style=${styleMap(styles)}>${this.value}</span>`;
  }

  private _onInput(e: Event) {
    this.value = (e.target as HTMLInputElement).value;
  }

  private _onChange() {
    this.dispatchEvent(new CustomEvent('change', { detail: this.value, bubbles: true }));
  }

  private _ensureValidValue(value: string | number, min?: number | string | null, max?: number | string | null) {
    if (value === '') {
      return value;
    }
    min = min ?? this.min;
    max = max ?? this.max;

    if (typeof value === 'string') {
      value = parseFloat(value);
    }
    if (typeof min === 'string') {
      min = parseFloat(min);
    }
    if (typeof max === 'string') {
      max = parseFloat(max);
    }

    if (value < min) {
      value = min;
    } else if (value > max) {
      value = max;
    }

    return value.toString();
  }
}

window.customElements.define('zui-slider', ZuiSlider);

declare global {
  interface HTMLElementTagNameMap {
    'zui-slider': ZuiSlider;
  }
}
